import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Card, Drawer, Flex, Result, Spin } from 'antd';
import { useState } from 'react';
import MarkdownContent from '@/common/components/MarkdownContent';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import {
  createIpCreatorRecord,
  queryLatestIpKernel,
  updateIpCreatorRecord,
} from '../services/ip-creator';
import SkillChat from '../components/SkillChat';

const RESOURCE = 'ip-kernels';
const RESOURCE_NAME = '创作内核';

const isIpKernelOutput = (value) =>
  value && typeof value.ipKernel === 'string' && typeof value.ipKernelSnapshot === 'string';

const IpKernels = () => {
  const [editing, setEditing] = useState(false);
  const { organizationId } = useOrganizationPath();
  const { message } = App.useApp();

  const {
    data: ipKernelRecord,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['ip-creator', RESOURCE, organizationId, 'latest'],
    queryFn: () => queryLatestIpKernel({ organizationId }),
    enabled: Boolean(organizationId),
  });

  const { mutateAsync: createRecord, isPending: creating } = useMutation({
    mutationFn: (payload) => createIpCreatorRecord(RESOURCE, payload),
    onSuccess: () => message.success(`${RESOURCE_NAME}已创建`),
    onError: () => message.error(`${RESOURCE_NAME}创建失败，请重试`),
  });

  const { mutateAsync: updateRecord, isPending: updating } = useMutation({
    mutationFn: (payload) => updateIpCreatorRecord(RESOURCE, payload),
    onSuccess: () => message.success(`${RESOURCE_NAME}已更新`),
    onError: () => message.error(`${RESOURCE_NAME}更新失败，请重试`),
  });

  const handleComplete = async (output) => {
    try {
      if (ipKernelRecord?.id) {
        await updateRecord({
          data: {
            id: ipKernelRecord.id,
            organizationId,
            ...output,
          },
        });
      } else {
        await createRecord({
          data: {
            organizationId,
            ...output,
          },
        });
      }
      await refetch();
      setEditing(false);
    } catch {
      // useMutation onError handles user feedback.
    }
  };

  const actionText = ipKernelRecord ? '更新' : '创建';
  const description = ipKernelRecord?.updatedAt
    ? `${ipKernelRecord.userName || '-'} ${new Date(ipKernelRecord.updatedAt).toLocaleString()} 更新`
    : '暂无创作内核，快来创建吧！';

  return (
    <PageContainer
      content={description}
      extra={
        ipKernelRecord ? (
          <Button type="primary" icon={<EditOutlined />} onClick={() => setEditing(true)}>
            {actionText}
          </Button>
        ) : null
      }
    >
      <Flex vertical gap="large">
        <Spin spinning={isLoading}>
          {ipKernelRecord?.ipKernel ? (
            <Card variant="borderless">
              <MarkdownContent>{ipKernelRecord.ipKernel}</MarkdownContent>
            </Card>
          ) : (
            <Card variant="borderless">
              <Result
                status="info"
                title={`${RESOURCE_NAME}`}
                subTitle="系统基于创作内核进行热点采集、选题策划、文章创作、视频脚本和质量检查。"
                extra={
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing(true)}>
                    {actionText}
                  </Button>
                }
              />
            </Card>
          )}
        </Spin>
      </Flex>
      <Drawer
        destroyOnHidden
        title={`${actionText}${RESOURCE_NAME}`}
        open={editing}
        size={1024}
        closable={{ placement: 'end' }}
        mask={{ closable: false }}
        onClose={() => setEditing(false)}
      >
        {editing ? (
          <Spin spinning={creating || updating}>
            <SkillChat
              height="calc(100vh - 105px)"
              input={ipKernelRecord?.ipKernel ? { ipKernel: ipKernelRecord.ipKernel } : {}}
              isOutputValid={isIpKernelOutput}
              skillName="skill-ip-kernel"
              startPrompt={`咱们现在开始${actionText} IP 的创作内核。`}
              strings={{
                composer: {
                  input: {
                    placeholder: '请输入你关于 IP 创作内核的思考',
                  },
                },
              }}
              onComplete={handleComplete}
            />
          </Spin>
        ) : null}
      </Drawer>
    </PageContainer>
  );
};

export default IpKernels;
